/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useQuery } from '@tanstack/react-query';

/**
 * `UsersMetrics` type.
 */

export type UsersMetrics = {
  newUsersCount: number;
  activeUsersCount: number;
  usersWithActiveTherapiesCount: number;
  newTherapiesCount: number;
  churnedUsersCount: number;
  retentionRate: number | null;
  averageTherapyDurationDays: number | null;
};

/**
 * `UsersMetricsOptions` type.
 */

export type UsersMetricsOptions = {
  accessType?: string | string[];
  churnFrom?: string;
  requireLastSeen?: boolean;
};

/**
 * Get users metrics.
 */

function getUsersMetrics(
  client: AxiosInstance,
  from: string,
  to: string,
  options?: UsersMetricsOptions
): Promise<UsersMetrics> {
  return client.get(resolveAppUrl('metricsUsers', { params: { from, to, ...options } })).then(({ data }) => data?.data);
}

/**
 * Export `useGetUsersMetrics`.
 */

export function useGetUsersMetrics(from: string | null, to: string | null, options?: UsersMetricsOptions) {
  const client = useRequestProvider();

  return useQuery(['metricsUsers', from, to, options], {
    queryFn: () => getUsersMetrics(client, from!, to!, options),
    enabled: !!(from && to)
  });
}
